import React, { useState } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, Typography, Box, Divider, Grid, TextField } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { CommonPatientDetails } from '../../Components/Common Components/CommonComponents'
import CommonValidation from '../../Components/CommonFunctions/CommonValidation'
import { AmountFormat, ContentInTooltip } from '../../Components/CommonFunctions/CommonFunctions'
import RestAPIService from '../../Utility/Services/RestAPIService'
import { Serviceurls } from '../../Utility/API/Serviceurls'
import ToastMsg from '../../Components/ToastMsg/ToastMsg'

const LabelValueRow = ({ label, value, isHighlight = false, tooltip = false, tooltipLen = 25 }) => (
  <Grid container spacing={1} alignItems="center" sx={{ marginBottom: '0.3vw' }}>
    <Grid item xs={4}>
      <Typography sx={{ fontSize: '0.8vw', fontWeight: 500, color: '#616161' }}>{label}</Typography>
    </Grid>
    <Grid item xs={0.5}>
      <Typography sx={{ fontSize: '0.8vw', color: '#616161' }}>:</Typography>
    </Grid>
    <Grid item xs={7.5}>
      <Typography sx={{ fontSize: '0.85vw', fontWeight: isHighlight ? 600 : 500, color: '#333' }}>
        {tooltip ? ContentInTooltip(value, tooltipLen, '0.85vw') : value}
      </Typography>
    </Grid>
  </Grid>
)

export default function RTDiscApprovalPopup({ open, onClose, data }) {
  const [errorMsg, seterrorMsg] = useState(false)
  const [errorMsgText, seterrorMsgText] = useState('')
  const [successMsg, setsuccessMsg] = useState(false)
  const [successMsgText, setsuccessMsgText] = useState('')

  const errorMessage = (msg) => {
    seterrorMsgText(msg)
    seterrorMsg(true)
  }

  const successMessage = (msg) => {
    setsuccessMsgText(msg)
    setsuccessMsg(true)
  }

  const msgClose = () => {
    seterrorMsg(false)
    seterrorMsgText('')
    setsuccessMsg(false)
    setsuccessMsgText('')
  }

  const [rtdata, setRtdata] = useState({
    therapy_name: data?.therapy_name || '-',
    technique: data?.tachnique_name || '-',
    doctor_name: data?.doctor_name || '-',
    package_name: data?.package_name || '-',
    package_amount: data?.billing_details?.sub_total_amount || "0",
    discount_percentage: data?.billing_details?.discount_percentage || "0",
    discount_amount: data?.billing_details?.discount_amount || "0",
    discount_comments: data?.billing_details?.discount_comments || '-',
    net_amount: data?.billing_details?.net_amount || "0",
  })

  const postTransmitDetails = (Status = "") => {
    try {
      const { billing_details, ...rest } = data;
      let PayloadData = {
        ...rest,
        "billing_details": { ...billing_details, request_status: Status},
        'patient_radiotherapy_id': data?.id || null,
        'sub_total_amount': +rtdata?.package_amount || 0,
        'package_amount': Status === "Rejected" ? (+rtdata?.package_amount || 0) : (+rtdata?.net_amount || 0),
        'discount_percentage': Status === "Rejected" ? 0 : +rtdata?.discount_percentage || 0,
        'discount_amount': Status === "Rejected" ? 0 : +rtdata?.discount_amount || 0,
        'discount_comments': rtdata?.discount_comments || "",
        'request_status': Status,
        'doctor_id' : data?.doctor_id || null,
      }

      RestAPIService.create(PayloadData, Serviceurls.RT_DOCTOR_TRANSMIT_APPROVAL)
        .then((response) => {
          if (response.data.status === 'success') {
            successMessage(response.data.message)
            onClose(true)
          }
        }).catch(e => {
          if (e?.response?.data?.status === 'fail') {
            errorMessage(e.response.data.message)
          } else {
            errorMessage(e.message)
          }
        })
    } catch (e) {
      errorMessage(e.message)
    }
  }

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.4vw 1vw',
      }}>
        <Typography sx={{ fontSize: '1vw', fontWeight: 600 }}>RT Discount Approval</Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ fontSize: '1.2vw' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: '0.8vw 1vw' }}>
        <CommonPatientDetails data={data} />

        <Box sx={{ backgroundColor: '#f5f9fa', borderRadius: '0.4vw', padding: '0.6vw 0.8vw', marginTop: '0.6vw' }}>
          <Typography sx={{ fontSize: '0.85vw', fontWeight: 600, marginBottom: '0.4vw' }}>Treatment Details</Typography>
          <LabelValueRow label="Therapy" value={rtdata.therapy_name} tooltip tooltipLen={30} />
          <LabelValueRow label="Technique" value={rtdata.technique} tooltip tooltipLen={30} />
          <LabelValueRow label="Package Name" value={rtdata.package_name} tooltip tooltipLen={30} />
          <LabelValueRow label="Package Amount" value={AmountFormat(rtdata.package_amount || 0)} isHighlight />
          <LabelValueRow label="Doctor Name" value={rtdata.doctor_name} tooltip tooltipLen={30} />
        </Box>

        <Box sx={{ backgroundColor: '#fff8f0', borderRadius: '0.4vw', padding: '0.6vw 0.8vw', marginTop: '0.5vw', border: '1px solid #ffe0b2' }}>
          <Typography sx={{ fontSize: '0.85vw', fontWeight: 600, color: '#e65100', marginBottom: '0.5vw' }}>Discount Details</Typography>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Discount %"
                value={rtdata.discount_percentage}
                disabled={!rtdata.package_amount}
                InputProps={{ sx: { fontSize: '0.85vw' } }}
                InputLabelProps={{ sx: { fontSize: '0.8vw' } }}
                onChange={(e) => {
                  let percent = e.target.value;
                  if (percent === "") {
                    setRtdata((prev) => ({ ...prev, discount_percentage: "", discount_amount: "" }));
                  } else {
                    let isValid = CommonValidation.DecimalNumber(percent);
                    let numericValue = parseFloat(percent);
                    if (isValid && numericValue >= 0 && numericValue <= 100) {
                      const pkgAmt = parseFloat(rtdata.package_amount) || 0;
                      const discAmt = Math.round((pkgAmt * numericValue) / 100);
                      setRtdata((prev) => ({
                        ...prev,
                        discount_percentage: percent,
                        discount_amount: discAmt,
                        net_amount: (pkgAmt - discAmt).toFixed(2)
                      }))
                    }
                  }
                }}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Discount Amount"
                value={rtdata.discount_amount}
                disabled={!rtdata.package_amount}
                InputProps={{ sx: { fontSize: '0.85vw' } }}
                InputLabelProps={{ sx: { fontSize: '0.8vw' } }}
                onChange={(e) => {
                  let discAmt = e.target.value;
                  if (discAmt === "") {
                    setRtdata((prev) => ({ ...prev, discount_percentage: "", discount_amount: "" }));
                  } else {
                    let isValid = CommonValidation.numberOnly(discAmt);

                    if (isValid) {
                      let numericValue = parseInt(discAmt) || 0;
                      const pkgAmt = parseFloat(rtdata.package_amount) || 0;

                      if (numericValue < 0) numericValue = 0;
                      if (numericValue > pkgAmt) numericValue = pkgAmt;

                      const percent = pkgAmt > 0
                        ? ((numericValue / pkgAmt) * 100).toFixed(2)
                        : 0;

                      setRtdata((prev) => ({
                        ...prev,
                        discount_percentage: percent,
                        discount_amount: numericValue,
                        net_amount: (pkgAmt - numericValue).toFixed(2)
                      }));
                    }
                  }
                }}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
          <Typography sx={{ fontSize: '0.75vw', color: '#888', marginTop: '0.4vw' }}>
            {ContentInTooltip(`Comments: ${rtdata?.discount_comments}`, 70, '0.8vw')}
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: '0.4vw', padding: '0.5vw 0.8vw', marginTop: '0.5vw',
        }}>
          <Typography sx={{ fontSize: '0.9vw', fontWeight: 600, color: '#333' }}>Net Amount</Typography>
          <Typography sx={{ fontSize: '1vw', fontWeight: 700 }}>
            {AmountFormat(rtdata.net_amount || 0)}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: '0.5vw 1vw', justifyContent: 'flex-end', gap: '0.5vw' }}>
        <Button
          variant="outlined"
          size="small"
          color="error"
          sx={{ textTransform: 'capitalize', fontSize: '0.8vw', minWidth: '5vw' }}
          onClick={() => postTransmitDetails("Rejected")}
        >
          Reject
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ textTransform: 'capitalize', fontSize: '0.8vw', minWidth: '5vw', backgroundColor: '#04B7B1', '&:hover': { backgroundColor: '#039e99' } }}
          onClick={() => postTransmitDetails("Approved")}
        >
          Approve
        </Button>
      </DialogActions>
      {errorMsg && <ToastMsg severity={'error'} msg={errorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity={'success'} msg={successMsgText} msgPop={msgClose} />}
    </Dialog>
  )
}
